import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as providerService from "@/shared/api/services/providerService";
import { motion } from "framer-motion";
import {
  Search, Server, CheckCircle2, XCircle, AlertTriangle,
  Wrench, Eye, RefreshCw, Activity, TrendingUp, Zap,
} from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getProviderName } from "@/shared/utils";

/* ── Config ── */
const statusConfig = {
  active:      { icon: CheckCircle2, color: "#10b981", bg: "rgba(16,185,129,0.1)",  border: "rgba(16,185,129,0.22)",  dot: "#10b981", label: "Active"      },
  inactive:    { icon: XCircle,      color: "#64748b", bg: "rgba(100,116,139,0.09)",border: "rgba(100,116,139,0.18)", dot: "#475569", label: "Inactive"    },
  maintenance: { icon: Wrench,       color: "#f59e0b", bg: "rgba(245,158,11,0.09)", border: "rgba(245,158,11,0.2)",  dot: "#f59e0b", label: "Maintenance" },
  degraded:    { icon: AlertTriangle,color: "#f97316", bg: "rgba(249,115,22,0.09)", border: "rgba(249,115,22,0.2)",  dot: "#f97316", label: "Degraded"    },
};

const typeColors = {
  bank:    { bg: "rgba(56,189,248,0.09)",  border: "rgba(56,189,248,0.2)",  color: "#38bdf8" },
  ewallet: { bg: "rgba(168,85,247,0.09)",  border: "rgba(168,85,247,0.2)",  color: "#c084fc" },
  qr:      { bg: "rgba(16,185,129,0.09)",  border: "rgba(16,185,129,0.2)",  color: "#34d399" },
  card:    { bg: "rgba(245,158,11,0.09)",  border: "rgba(245,158,11,0.2)",  color: "#fbbf24" },
};

/* ── Summary Stat Card — matching dashboard style ── */
function SummaryCard({ label, value, color, icon: Icon, delay = 0 }) {
  const [hov, setHov] = useState(false);
  const [gp, setGp] = useState({ x: 50, y: 50 });
  const ref = React.useRef(null);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, type: "spring", stiffness: 210, damping: 22 }}
      onHoverStart={() => setHov(true)}
      onHoverEnd={() => setHov(false)}
      onMouseMove={(e) => {
        const r = ref.current?.getBoundingClientRect();
        if (r) setGp({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
      }}
      whileHover={{ y: -5, scale: 1.025, transition: { type: "spring", stiffness: 280, damping: 22 } }}
      style={{
        position: "relative", overflow: "hidden", borderRadius: "18px", padding: "20px",
        cursor: "default", fontFamily: "'Plus Jakarta Sans', sans-serif",
        background: "linear-gradient(150deg, rgba(8,16,34,0.98) 0%, rgba(11,22,44,0.96) 100%)",
        border: hov ? `1px solid ${color}42` : "1px solid rgba(255,255,255,0.054)",
        boxShadow: hov ? `0 16px 48px -8px ${color}28, 0 0 0 1px ${color}16` : "0 3px 18px rgba(0,0,0,0.38)",
        transition: "border 0.35s ease, box-shadow 0.35s ease",
      }}
    >
      {/* Cursor radial glow */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `radial-gradient(circle at ${gp.x}% ${gp.y}%, ${color}16 0%, transparent 60%)`, opacity: hov ? 1 : 0, transition: "opacity 0.3s ease" }} />
      <div style={{ position: "absolute", top: 0, right: 0, width: "70px", height: "70px", background: `radial-gradient(circle at 100% 0%, ${color}10 0%, transparent 70%)`, transform: "translate(20%,-20%)", pointerEvents: "none" }} />

      {/* Bottom accent line */}
      <motion.div
        style={{ position: "absolute", bottom: 0, left: 0, height: "2.5px", background: `linear-gradient(90deg, ${color}dd, ${color}66, transparent)` }}
        animate={{ width: hov ? "100%" : "0%", opacity: hov ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
      />

      <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: `${color}90`, marginBottom: "9px" }}>{label}</p>
          <motion.p
            style={{ fontSize: "26px", fontWeight: 900, color: "#f0f9ff", letterSpacing: "-0.04em", margin: 0 }}
            animate={{ scale: hov ? 1.05 : 1 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
          >{value}</motion.p>
        </div>

        <motion.div
          animate={{ rotate: hov ? [0, -8, 5, -2, 0] : 0, scale: hov ? 1.15 : 1, y: hov ? -2 : 0 }}
          transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <div style={{
            width: "44px", height: "44px", borderRadius: "13px",
            background: `${color}14`, border: `1px solid ${color}30`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: hov ? `0 8px 22px ${color}22` : "none", transition: "box-shadow 0.3s ease",
          }}>
            <Icon style={{ width: "18px", height: "18px", color }} />
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

/* ── Provider Card ── */
function ProviderCard({ provider, index, translateStatus, translateType, language }) {
  const [hov, setHov] = useState(false);
  const [gp, setGp] = useState({ x: 50, y: 50 });
  const ref = React.useRef(null);

  const status = statusConfig[provider.status] || statusConfig.inactive;
  const type = typeColors[provider.type] || typeColors.bank;
  const StatusIcon = status.icon;
  const name = getProviderName(provider, language);
  const successRate = provider.success_rate ?? 100;
  const responseTime = provider.avg_response_time ?? 0;
  const ACCENT = "#38bdf8";

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: index * 0.06, type: "spring", stiffness: 200, damping: 22 }}
      onHoverStart={() => setHov(true)}
      onHoverEnd={() => setHov(false)}
      onMouseMove={(e) => {
        const r = ref.current?.getBoundingClientRect();
        if (r) setGp({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
      }}
      whileHover={{ y: -7, scale: 1.02, transition: { type: "spring", stiffness: 260, damping: 22 } }}
      style={{
        position: "relative", overflow: "hidden", borderRadius: "20px", padding: "22px",
        cursor: "default", fontFamily: "'Plus Jakarta Sans', sans-serif",
        background: "linear-gradient(150deg, rgba(8,16,34,0.98) 0%, rgba(11,22,44,0.96) 100%)",
        border: hov ? `1px solid ${ACCENT}3e` : "1px solid rgba(255,255,255,0.054)",
        boxShadow: hov
          ? `0 20px 56px -10px rgba(56,189,248,0.2), 0 0 0 1px rgba(56,189,248,0.1)`
          : "0 4px 20px rgba(0,0,0,0.38)",
        transition: "border 0.35s ease, box-shadow 0.35s ease",
      }}
    >
      {/* Cursor radial glow */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `radial-gradient(circle at ${gp.x}% ${gp.y}%, rgba(56,189,248,0.1) 0%, transparent 62%)`, opacity: hov ? 1 : 0, transition: "opacity 0.3s ease" }} />

      {/* Top ambient line */}
      <motion.div
        style={{ position: "absolute", top: 0, left: 0, right: 0, height: "1px", background: "linear-gradient(90deg, transparent, rgba(56,189,248,0.45), rgba(99,102,241,0.35), transparent)" }}
        animate={{ opacity: hov ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      />

      {/* Corner atmosphere */}
      <div style={{ position: "absolute", top: 0, right: 0, width: "90px", height: "90px", background: "radial-gradient(circle at 100% 0%, rgba(56,189,248,0.08) 0%, transparent 70%)", transform: "translate(25%,-25%)", pointerEvents: "none" }} />

      {/* Bottom accent line */}
      <motion.div
        style={{ position: "absolute", bottom: 0, left: 0, height: "2.5px", background: "linear-gradient(90deg, rgba(56,189,248,0.9), rgba(99,102,241,0.7), transparent)" }}
        animate={{ width: hov ? "100%" : "0%", opacity: hov ? 1 : 0 }}
        transition={{ duration: 0.42, ease: [0.34, 1.56, 0.64, 1] }}
      />

      {/* Header row */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* Icon with tilt */}
          <motion.div
            animate={{ rotate: hov ? [0, -8, 5, -2, 0] : 0, scale: hov ? 1.12 : 1, y: hov ? -2 : 0 }}
            transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <div style={{
              width: "46px", height: "46px", borderRadius: "14px", flexShrink: 0,
              background: "linear-gradient(135deg, rgba(56,189,248,0.16), rgba(99,102,241,0.1))",
              border: "1px solid rgba(56,189,248,0.22)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: hov ? "0 8px 24px rgba(56,189,248,0.2)" : "none",
              transition: "box-shadow 0.3s ease",
            }}>
              <Server style={{ width: "20px", height: "20px", color: "#38bdf8" }} />
            </div>
          </motion.div>

          <div>
            <h3 style={{ margin: 0, fontSize: "14px", fontWeight: 800, color: "#f0f9ff", lineHeight: 1.2, letterSpacing: "-0.01em" }}>{name}</h3>
            <p style={{ margin: "3px 0 0", fontSize: "11px", color: "#334155", fontFamily: "'JetBrains Mono', monospace" }}>{provider.code}</p>
          </div>
        </div>

        {/* Status pulse dot */}
        <div style={{ position: "relative", width: "12px", height: "12px", marginTop: "5px", flexShrink: 0 }}>
          <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: status.dot, opacity: 0.28, animation: provider.status === "active" ? "dotPulse 2s ease-in-out infinite" : "none" }} />
          <div style={{ width: "12px", height: "12px", borderRadius: "50%", background: status.dot, boxShadow: `0 0 8px ${status.dot}80` }} />
        </div>
      </div>

      {/* Badges */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "18px" }}>
        <motion.span whileHover={{ scale: 1.04 }}
          style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "4px 10px", borderRadius: "20px", background: type.bg, border: `1px solid ${type.border}`, fontSize: "11px", fontWeight: 700, color: type.color }}>
          {translateType(provider.type)}
        </motion.span>
        <motion.span whileHover={{ scale: 1.04 }}
          style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "4px 10px", borderRadius: "20px", background: status.bg, border: `1px solid ${status.border}`, fontSize: "11px", fontWeight: 700, color: status.color }}>
          <StatusIcon style={{ width: "11px", height: "11px" }} />
          {translateStatus(provider.status)}
        </motion.span>
      </div>

      {/* Metric cards */}
      <div style={{ position: "relative", zIndex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "16px" }}>
        {/* Success rate */}
        <div style={{ background: "rgba(4,8,18,0.65)", borderRadius: "13px", padding: "12px", border: "1px solid rgba(56,189,248,0.07)", transition: "border 0.22s ease" }}>
          <p style={{ margin: "0 0 5px", fontSize: "9px", color: "#1e3050", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em" }}>Success Rate</p>
          <p style={{ margin: 0, fontSize: "20px", fontWeight: 900, letterSpacing: "-0.03em", color: successRate >= 95 ? "#10b981" : successRate >= 80 ? "#f59e0b" : "#ef4444" }}>
            {successRate}%
          </p>
          <div style={{ marginTop: "7px", height: "3px", borderRadius: "4px", background: "rgba(56,189,248,0.07)", overflow: "hidden" }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${successRate}%` }}
              transition={{ delay: 0.2 + index * 0.05, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              style={{ height: "100%", borderRadius: "4px", background: successRate >= 95 ? "#10b981" : successRate >= 80 ? "#f59e0b" : "#ef4444" }}
            />
          </div>
        </div>

        {/* Response time */}
        <div style={{ background: "rgba(4,8,18,0.65)", borderRadius: "13px", padding: "12px", border: "1px solid rgba(56,189,248,0.07)" }}>
          <p style={{ margin: "0 0 5px", fontSize: "9px", color: "#1e3050", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em" }}>Response</p>
          <div style={{ display: "flex", alignItems: "baseline", gap: "3px" }}>
            <p style={{ margin: 0, fontSize: "20px", fontWeight: 900, letterSpacing: "-0.03em", color: "#f0f9ff" }}>{responseTime}</p>
            <span style={{ fontSize: "11px", color: "#334155", fontWeight: 600 }}>ms</span>
          </div>
          <div style={{ marginTop: "7px", display: "flex", alignItems: "center", gap: "4px" }}>
            <Activity style={{ width: "11px", height: "11px", color: "#38bdf8", opacity: 0.55 }} />
            <span style={{ fontSize: "9px", color: "#1e3050", fontWeight: 600 }}>avg latency</span>
          </div>
        </div>
      </div>

      {/* Support badges */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", gap: "6px", flexWrap: "wrap", paddingTop: "14px", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
        {provider.supports_deposit && (
          <motion.span whileHover={{ scale: 1.06, y: -1 }}
            style={{ fontSize: "10px", fontWeight: 700, color: "#38bdf8", background: "rgba(56,189,248,0.08)", padding: "3px 9px", borderRadius: "7px", border: "1px solid rgba(56,189,248,0.16)" }}>
            Deposit
          </motion.span>
        )}
        {provider.supports_withdrawal && (
          <motion.span whileHover={{ scale: 1.06, y: -1 }}
            style={{ fontSize: "10px", fontWeight: 700, color: "#c084fc", background: "rgba(168,85,247,0.08)", padding: "3px 9px", borderRadius: "7px", border: "1px solid rgba(168,85,247,0.16)" }}>
            Withdrawal
          </motion.span>
        )}
        {provider.supports_qr && (
          <motion.span whileHover={{ scale: 1.06, y: -1 }}
            style={{ fontSize: "10px", fontWeight: 700, color: "#34d399", background: "rgba(16,185,129,0.08)", padding: "3px 9px", borderRadius: "7px", border: "1px solid rgba(16,185,129,0.16)" }}>
            QR
          </motion.span>
        )}
      </div>
    </motion.div>
  );
}

/* ── MAIN ── */
export default function ResellerProviders() {
  const [search, setSearch] = useState("");
  const [sfocus, setSfocus] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const resellerName = "Asia Gaming Network";
  const { language, t } = useLanguage();

  const translateStatus = (s) => ({ active: t("active"), inactive: t("inactive"), degraded: t("degraded"), maintenance: t("maintenance") })[s] || s;
  const translateType = (tp) => ({ bank: t("bank"), ewallet: t("ewallet"), qr: t("qr"), card: t("card") })[tp] || tp;

  const { data: providers = [], isLoading, refetch } = useQuery({
    queryKey: ["providers"],
    queryFn: () => providerService.getAllProviders(),
  });

  const filtered = providers.filter(p => {
    const name = getProviderName(p, language);
    return name?.toLowerCase().includes(search.toLowerCase()) || p.code?.toLowerCase().includes(search.toLowerCase());
  });

  const activeCount    = providers.filter(p => p.status === "active").length;
  const degradedCount  = providers.filter(p => p.status === "degraded").length;
  const maintCount     = providers.filter(p => p.status === "maintenance").length;

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600&display=swap');
    .rp-page { font-family: 'Plus Jakarta Sans', sans-serif; max-width: 1400px; }
    .rp-stat-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 12px; }
    .rp-card-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 16px; }
    @media(min-width:640px)  { .rp-stat-grid { grid-template-columns: repeat(4,1fr); } }
    @keyframes dotPulse { 0%,100%{ transform:scale(1); opacity:0.28; } 50%{ transform:scale(2.6); opacity:0; } }
    @keyframes rp-spin   { to { transform: rotate(360deg); } }
    @keyframes rp-pulse-hdr { 0%,100%{ box-shadow:0 0 0 0 rgba(56,189,248,0.5); } 50%{ box-shadow:0 0 0 5px rgba(56,189,248,0); } }
  `;

  return (
    <ResellerLayout currentPage="ResellerProviders" resellerName={resellerName}>
      <style>{css}</style>
      <div className="rp-page">

        {/* HEADER */}
        <motion.div initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.46, ease: [0.22, 1, 0.36, 1] }}
          style={{ marginBottom: "24px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <motion.div
              animate={{ y: [0, -4, 0] }}
              transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut" }}
              style={{ width: "46px", height: "46px", borderRadius: "14px", background: "linear-gradient(135deg, #0ea5e9, #38bdf8)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 22px rgba(14,165,233,0.45), inset 0 1px 0 rgba(255,255,255,0.18)" }}>
              <Server style={{ width: "20px", height: "20px", color: "#fff" }} />
            </motion.div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "2px" }}>
                <h1 style={{ fontSize: "24px", fontWeight: 900, color: "#f0f9ff", letterSpacing: "-0.04em", margin: 0 }}>
                  {t("providers") || "Providers"}
                </h1>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "3px 10px", borderRadius: "20px", background: "rgba(56,189,248,0.09)", border: "1px solid rgba(56,189,248,0.2)", fontSize: "10px", fontWeight: 700, color: "#38bdf8" }}>
                  <Eye style={{ width: "10px", height: "10px" }} />
                  {t("viewOnly") || "View Only"}
                </span>
              </div>
              <p style={{ fontSize: "12px", color: "#334155", margin: 0, fontWeight: 500 }}>
                <span style={{ color: "#10b981", fontWeight: 700 }}>{activeCount}</span> active · {providers.length} total
              </p>
            </div>
          </div>

          <motion.button
            onClick={async () => { setRefreshing(true); await refetch(); setTimeout(() => setRefreshing(false), 600); }}
            whileHover={{ scale: 1.06, boxShadow: "0 8px 24px rgba(56,189,248,0.2)" }}
            whileTap={{ scale: 0.94 }}
            style={{ display: "flex", alignItems: "center", gap: "7px", padding: "10px 18px", borderRadius: "13px", background: "rgba(56,189,248,0.07)", border: "1px solid rgba(56,189,248,0.22)", color: "#38bdf8", fontSize: "13px", fontWeight: 700, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", outline: "none" }}>
            <motion.div animate={{ rotate: refreshing ? 360 : 0 }} transition={{ duration: 0.7, ease: "linear", repeat: refreshing ? Infinity : 0 }}>
              <RefreshCw style={{ width: "13px", height: "13px" }} />
            </motion.div>
            Refresh
          </motion.button>
        </motion.div>

        {/* SUMMARY STAT CARDS */}
        <div className="rp-stat-grid" style={{ marginBottom: "22px" }}>
          <SummaryCard label="Active"      value={activeCount}   color="#10b981" icon={CheckCircle2} delay={0}    />
          <SummaryCard label="Total"       value={providers.length} color="#38bdf8" icon={Server}    delay={0.07} />
          <SummaryCard label="Degraded"    value={degradedCount} color="#f97316" icon={AlertTriangle} delay={0.14} />
          <SummaryCard label="Maintenance" value={maintCount}    color="#f59e0b" icon={Wrench}       delay={0.21} />
        </div>

        {/* SEARCH */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}
          style={{ marginBottom: "20px", maxWidth: "380px" }}>
          <div style={{ position: "relative" }}>
            <Search style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", width: "14px", height: "14px", color: sfocus ? "#38bdf8" : "#334155", transition: "color 0.22s ease" }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onFocus={() => setSfocus(true)}
              onBlur={() => setSfocus(false)}
              placeholder={t("searchProviders") || "Search providers…"}
              style={{
                width: "100%", padding: "10px 14px 10px 38px", borderRadius: "13px",
                background: sfocus ? "rgba(56,189,248,0.05)" : "rgba(8,16,34,0.9)",
                border: sfocus ? "1px solid rgba(56,189,248,0.4)" : "1px solid rgba(255,255,255,0.07)",
                color: "#c8dff0", fontSize: "13px", outline: "none",
                boxShadow: sfocus ? "0 0 0 3px rgba(56,189,248,0.08)" : "none",
                transition: "all 0.25s ease", fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            />
          </div>
        </motion.div>

        {/* GRID */}
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "80px", display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.9, ease: "linear", repeat: Infinity }}>
              <Server style={{ width: "28px", height: "28px", color: "#38bdf8" }} />
            </motion.div>
            <span style={{ fontSize: "13px", color: "#334155", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Loading providers…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px", display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
            <motion.div animate={{ y: [0, -6, 0] }} transition={{ repeat: Infinity, duration: 2.5 }}
              style={{ width: "56px", height: "56px", borderRadius: "18px", background: "rgba(56,189,248,0.06)", border: "1px solid rgba(56,189,248,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Server style={{ width: "22px", height: "22px", color: "#1e3a52" }} />
            </motion.div>
            <p style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "#1e2d45", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>No providers found</p>
          </div>
        ) : (
          <div className="rp-card-grid">
            {filtered.map((p, i) => (
              <ProviderCard key={p.id} provider={p} index={i} translateStatus={translateStatus} translateType={translateType} language={language} />
            ))}
          </div>
        )}
      </div>
    </ResellerLayout>
  );
}